import React, { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, User, CreditCard, Eye, Edit, FileText, TrendingUp, RefreshCw, Shield } from 'lucide-react';
import { formatCurrency as marketFormatCurrency } from '@/lib/market';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminFamilyManagement = ({ user, onLogout }) => {
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [owners, setOwners] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ownersLoading, setOwnersLoading] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [stats, setStats] = useState({
    totalFamilies: 0,
    totalOwners: 0,
    totalAccounts: 0,
    totalAUM: 0
  });

  useEffect(() => {
    fetchFamilies();
  }, []);

  const fetchFamilies = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const response = await axios.get(`${API}/families`, config);
      const familiesData = response.data.families || response.data;
      setFamilies(familiesData);
      
      // Calculate admin-level statistics
      const totalAUM = familiesData.reduce((sum, family) => sum + (family.total_aum || 0), 0);
      setStats(prev => ({
        ...prev,
        totalFamilies: familiesData.length,
        totalAUM: totalAUM
      }));
      
    } catch (error) {
      console.error('Error fetching families:', error);
      toast.error('Failed to fetch families from Wealth Spectrum');
    } finally {
      setLoading(false);
    }
  };

  const fetchOwners = async (familyId) => {
    setOwnersLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      console.log(`Admin fetching owners for family: ${familyId}`);
      const response = await axios.get(`${API}/owners/family/${familyId}`, config);
      
      const ownersData = response.data.owners || response.data;
      console.log(`Admin received ${ownersData.length} owners for family ${familyId}`);
      
      setOwners(ownersData);
      setStats(prev => ({ ...prev, totalOwners: ownersData.length }));
      
    } catch (error) {
      console.error('Error fetching owners:', error);
      toast.error('Failed to fetch owners from Wealth Spectrum');
      setOwners([]);
    } finally {
      setOwnersLoading(false);
    }
  };

  const fetchAccounts = async (ownerId) => {
    setAccountsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      console.log(`Admin fetching accounts for owner: ${ownerId}`);
      const response = await axios.get(`${API}/accounts/owner/${ownerId}`, config);
      
      const accountsData = response.data.accounts || response.data;
      console.log(`Admin received ${accountsData.length} accounts for owner ${ownerId}`);
      
      setAccounts(accountsData);
      setStats(prev => ({ ...prev, totalAccounts: accountsData.length }));
      
    } catch (error) {
      console.error('Error fetching accounts:', error);
      toast.error('Failed to fetch accounts from Wealth Spectrum');
      setAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  };

  const handleFamilySelect = async (family) => {
    setSelectedFamily(family);
    setOwners([]);
    setAccounts([]);
    await fetchOwners(family.id);
  };

  const handleOwnerSelect = async (owner) => {
    setAccounts([]);
    await fetchAccounts(owner.id);
  };

  const handleRefreshData = () => {
    toast.info('Refreshing all system data from Wealth Spectrum...');
    
    // Reset all state
    setSelectedFamily(null);
    setOwners([]);
    setAccounts([]);
    
    // Fetch fresh data
    fetchFamilies();
  };

  const handleSyncWithWealthSpectrum = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      await axios.post(`${API}/wealth-spectrum/sync`, {}, config);
      toast.success('System data synchronized with Wealth Spectrum');
      fetchFamilies();
    } catch (error) {
      console.error('Error syncing with Wealth Spectrum:', error);
      toast.error('Failed to sync with Wealth Spectrum');
    }
  };

  const formatCurrency = (value) => marketFormatCurrency(value, user?.market);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ffd87a]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2" style={{fontFamily: "'PerfectNineties', serif"}}>
              System Family Management
            </h1>
            <p className="text-gray-600 flex items-center gap-2">
              <Shield className="w-4 h-4 text-ugmonk-dark" />
              Admin view - Real-time data from{' '}
              <a 
                href="https://www.wealthspectrum.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-ugmonk-dark hover:text-[#1a1a1a] font-medium"
              >
                Wealth Spectrum
              </a>
            </p>
          </div>
          
          {/* Admin Actions */}
          <div className="flex gap-2">
            <Button 
              onClick={handleRefreshData}
              variant="outline" 
              className="border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh All
            </Button>
            <Button 
              onClick={handleSyncWithWealthSpectrum}
              className="bg-ugmonk-yellow hover:bg-[#f5cc6a] text-black"
            >
              <TrendingUp className="w-4 h-4 mr-2" />
              Sync System
            </Button>
          </div>
        </div>

        {/* Admin Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-[#fffdf0] to-[#fffdf0]/50 rounded-xl p-6 border border-ugmonk-border">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <Users className="w-6 h-6 text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Families</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalFamilies}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-[#fffdf0] to-[#fffdf0]/50 rounded-xl p-6 border border-ugmonk-border">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-[#fffdf0]">
                <User className="w-6 h-6 text-ugmonk-dark" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Owners</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalOwners}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl p-6 border border-green-200">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-green-200">
                <CreditCard className="w-6 h-6 text-green-700" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Accounts</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalAccounts}</p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl p-6 border border-purple-200">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-purple-200">
                <TrendingUp className="w-6 h-6 text-purple-700" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total System AUM</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalAUM)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Families List */}
          <Card className="border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <Users className="w-5 h-5 text-ugmonk-dark" />
                All Families ({families.length})
                <span className="text-xs bg-[#fffdf0] text-ugmonk-dark px-2 py-1 rounded-full ml-auto">
                  System Wide
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-96 overflow-y-auto">
              {families.map((family) => (
                <div
                  key={family.id}
                  onClick={() => handleFamilySelect(family)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selectedFamily?.id === family.id
                      ? 'bg-[#fffdf0] border-[#ffd87a]/50'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                  }`}
                >
                  <div className="font-semibold text-gray-900 text-sm">{family.family_name}</div>
                  <div className="text-xs text-gray-600">Head: {family.family_head || 'Not specified'}</div>
                  <div className="text-xs text-gray-600">RM: {family.rm_name || 'Unassigned'}</div>
                  <div className="text-xs text-ugmonk-dark">AUM: {formatCurrency(family.total_aum || 0)}</div>
                </div>
              ))}
              {families.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No families found</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Owners List */}
          <Card className="border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <User className="w-5 h-5 text-ugmonk-dark" />
                Family Members ({owners.length})
                {selectedFamily && (
                  <span className="text-xs bg-[#fffdf0] text-ugmonk-dark px-2 py-1 rounded-full ml-auto">
                    {selectedFamily.family_name}
                  </span>
                )}
                {ownersLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1a1a1a] ml-auto"></div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-96 overflow-y-auto">
              {selectedFamily ? (
                owners.length > 0 ? (
                  owners.map((owner) => (
                    <div
                      key={owner.id}
                      className="p-3 rounded-lg border bg-gray-50 border-gray-200 hover:bg-gray-100 cursor-pointer"
                      onClick={() => handleOwnerSelect(owner)}
                    >
                      <div className="font-semibold text-gray-900 text-sm">{owner.owner_name}</div>
                      <div className="text-xs text-gray-600">PAN: {owner.pan}</div>
                      <div className="text-xs text-gray-600">Mobile: {owner.mobile}</div>
                      <div className="text-xs text-gray-600">Relationship: {owner.relationship || 'N/A'}</div>
                      <div className="text-xs text-ugmonk-dark">AUM: {formatCurrency(owner.total_aum || 0)}</div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>No owners in this family</p>
                  </div>
                )
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <User className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select a family to view owners</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Accounts List */}
          <Card className="border-gray-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-gray-900 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-ugmonk-dark" />
                Member Accounts ({accounts.length})
                {accountsLoading && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#ffd87a] ml-auto"></div>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 max-h-96 overflow-y-auto">
              {accounts.length > 0 ? (
                accounts.map((account) => (
                  <div
                    key={account.id}
                    className="p-3 rounded-lg border bg-gray-50 border-gray-200 hover:bg-gray-100"
                  >
                    <div className="font-semibold text-gray-900 text-sm">{account.account_name}</div>
                    <div className="text-xs text-gray-600">Type: {account.account_type}</div>
                    <div className="text-xs text-gray-600">Number: {account.account_number}</div>
                    <div className="text-xs text-gray-600">Advisor: {account.advisor_name}</div>
                    {account.is_held_away && (
                      <div className="text-xs text-ugmonk-dark font-medium">⚠️ Held Away Account</div>
                    )}
                    <div className="text-xs text-ugmonk-dark font-semibold">Value: {formatCurrency(account.total_value || 0)}</div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CreditCard className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Select an owner to view accounts</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Admin Insights */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100/50 rounded-xl p-6 border border-gray-200">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-lg bg-[#fffdf0]">
              <Shield className="text-ugmonk-dark" size={24} />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">System Administration</h3>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                Complete oversight of all client families, owners, and accounts managed through Wealth Spectrum. 
                This dashboard provides real-time system-wide visibility for administrative oversight and compliance monitoring.
              </p>
              <div className="text-xs text-gray-500 bg-white rounded-lg p-3 border border-gray-200">
                🔒 <strong>Admin Access:</strong> You have full system visibility across all RMs and client hierarchies. 
                Data is synchronized in real-time with Wealth Spectrum platform for accuracy and compliance.
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AdminFamilyManagement;
